import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { DownloadIcon, CheckCircle2Icon, ClockIcon, LayoutIcon, SettingsIcon, GlobeIcon, ShieldCheckIcon, MessageSquareIcon, BarChart3Icon, AlertCircleIcon, InfoIcon } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/8mGMBFzUfDBa2mdC9g2ZRk/AVY_LOGO.png';
export function ProposalPage() {
  const proposalRef = useRef<HTMLDivElement>(null);
  const downloadPDF = async () => {
    if (!proposalRef.current) return;
    const element = proposalRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: 1200
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = imgProps.height * pdfWidth / imgProps.width;
    // Add multiple pages if content is too long
    let heightLeft = pdfHeight;
    let position = 0;
    const pageHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pageHeight;
    while (heightLeft >= 0) {
      position = heightLeft - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;
    }
    pdf.save('AVY-Website-Proposal-Anita-Oberoi.pdf');
  };
  return <div className="min-h-screen bg-[#d9e2e8] p-2 md:p-4 font-sans selection:bg-accent selection:text-white" data-id="element-570">
      <div className="main-container max-w-[1600px] mx-auto bg-card rounded-[1.5rem] md:rounded-[2rem] border border-black/5 shadow-sm min-h-[calc(100vh-2rem)] flex flex-col" data-id="element-571">
        <Navigation activePath="/proposal" data-id="element-572" />

        <main className="flex-1 px-4 md:px-16 py-8 md:py-12 space-y-12" data-id="element-573">
          {/* Header Actions */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-574">
            <div className="space-y-1" data-id="element-575">
              <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground" data-id="element-576">
                Project Proposal
              </span>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-ink" data-id="element-577">
                Website Design & Build
              </h1>
            </div>
            <button onClick={downloadPDF} className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-bold text-sm shadow-sm hover:opacity-90 transition-all whitespace-nowrap" data-id="element-578">
              <DownloadIcon size={18} data-id="element-579" />
              Download PDF
            </button>
          </div>

          <div ref={proposalRef} className="bg-white rounded-[2rem] p-8 md:p-16 border border-black/5 shadow-sm space-y-20 text-ink" data-id="element-580">
            {/* Cover Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-b border-black/5 pb-12" data-id="element-581">
              <div className="space-y-6" data-id="element-582">
                <img src={LOGO_URL} alt="Avy Logo" className="h-10 w-auto" data-id="element-583" />
                <div className="space-y-1" data-id="element-584">
                  <p className="text-xs uppercase tracking-widest font-bold text-ink/40" data-id="element-585">
                    Prepared for
                  </p>
                  <p className="text-xl font-bold" data-id="element-586">Anita Oberoi</p>
                </div>
              </div>
              <div className="space-y-4 md:text-right" data-id="element-587">
                <div className="space-y-1" data-id="element-588">
                  <p className="text-xs uppercase tracking-widest font-bold text-ink/40" data-id="element-589">
                    Prepared by
                  </p>
                  <p className="text-lg font-bold text-accent" data-id="element-590">
                    AVY® Consultancy Limited
                  </p>
                </div>
                <div className="space-y-1" data-id="element-591">
                  <p className="text-xs uppercase tracking-widest font-bold text-ink/40" data-id="element-592">
                    Date
                  </p>
                  <p className="font-medium" data-id="element-593">June 2026</p>
                </div>
                <div className="space-y-1" data-id="element-594">
                  <p className="text-xs uppercase tracking-widest font-bold text-ink/40" data-id="element-595">
                    Proposal Reference
                  </p>
                  <p className="font-medium" data-id="element-596">AVY-WEB-2026-AO</p>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <section className="max-w-4xl space-y-8" data-id="element-597">
              <div className="flex items-center gap-3" data-id="element-598">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-599" />
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-600">
                  A note before we begin
                </h2>
              </div>
              <div className="space-y-6 text-xl leading-relaxed text-ink/80" data-id="element-601">
                <p className="font-bold text-ink text-2xl" data-id="element-602">
                  A website is rarely just a website.
                </p>
                <p data-id="element-603">
                  It is often the first signal people get about the standard of
                  your business. Before an enquiry, before a call, before trust
                  is built, people make a judgement. If what they find feels
                  unclear, dated or underwhelming, the business loses ground
                  before a conversation even starts.
                </p>
                <p data-id="element-604">
                  This project is about fixing that properly. The goal is to
                  create a website that looks credible, feels considered and
                  gives potential clients confidence in what you do.
                </p>
              </div>
            </section>

            {/* The Brief */}
            <section className="max-w-4xl space-y-8" data-id="element-605">
              <div className="flex items-center gap-3" data-id="element-606">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-607" />
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-608">
                  The brief
                </h2>
              </div>
              <div className="space-y-6 text-lg leading-relaxed text-ink/70" data-id="element-609">
                <p data-id="element-610">
                  This proposal outlines two ways to move the project forward.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4" data-id="element-611">
                  <div className="space-y-3" data-id="element-612">
                    <p className="font-bold text-ink text-xl" data-id="element-613">Website Sprint</p>
                    <p data-id="element-614">
                      Designed to get your business online quickly using a
                      predefined layout and structure, adapted to your brand,
                      content and priorities. It is a faster, more efficient
                      route to launch, but it is not a fully custom website
                      build.
                    </p>
                  </div>
                  <div className="space-y-3" data-id="element-615">
                    <p className="font-bold text-ink text-xl" data-id="element-616">
                      Strategic Website Build
                    </p>
                    <p data-id="element-617">
                      A more tailored, custom process with added strategy,
                      clearer content structure, more design refinement and a
                      longer delivery window.
                    </p>
                  </div>
                </div>
                <p className="pt-4 border-t border-black/5" data-id="element-618">
                  Both options are designed to create a clearer, more credible
                  digital presence that supports enquiries and reflects the
                  quality of your business properly.
                </p>
              </div>
            </section>

            {/* Project Options Table */}
            <section className="space-y-8" data-id="element-619">
              <div className="flex items-center gap-3" data-id="element-620">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-621" />
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-622">
                  Project options
                </h2>
              </div>
              <div className="overflow-x-auto -mx-8 md:mx-0 px-8 md:px-0" data-id="element-623">
                <table className="w-full border-collapse min-w-[800px]" data-id="element-624">
                  <thead data-id="element-625">
                    <tr className="bg-ink text-white" data-id="element-626">
                      <th className="p-5 text-left text-[10px] uppercase tracking-widest border border-white/10" data-id="element-627">
                        Feature
                      </th>
                      <th className="p-5 text-left text-[10px] uppercase tracking-widest border border-white/10" data-id="element-628">
                        Website Sprint
                      </th>
                      <th className="p-5 text-left text-[10px] uppercase tracking-widest border border-white/10" data-id="element-629">
                        Strategic Website Build
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm" data-id="element-630">
                    {[['Project fee', '£1,300 + VAT discounted rate', '£2,500 + VAT discounted rate'], ['Standard value', '£2,900 + VAT', '£3,500 + VAT'], ['Timeline', '14 days from kick-off', '4 weeks from kick-off'], ['Build type', 'Predefined layout adapted to your brand', 'More tailored custom website build'], ['Page structure', 'Agreed key pages and core information', 'Sitemap and clearer content structure'], ['Homepage design', 'Included', 'Included'], ['Inner pages', 'Up to 5 pages', 'Up to 8 pages'], ['Strategy session', 'Not included', '1 x strategy session'], ['Mobile responsive design', 'Included', 'Included'], ['SEO setup', 'Basic SEO setup', 'Enhanced on-page SEO setup'], ['Contact form and enquiry integration', 'Included', 'Included'], ['Email notification confirmation', 'Included', 'Included'], ['CMS setup (WordPress)', 'Included', 'Included'], ['CMS training', 'Documentation handover included', 'Live CMS training session plus documentation handover'], ['Shutterstock images', 'Up to 25 images', 'Up to 25 images'], ['Downloadable materials integration', 'Included', 'Included'], ['Revisions', 'One consolidated round per phase', 'Two consolidated revision rounds across the project'], ['Post-launch support', '7 days', '30 days'], ['Hosting', '£50/month + VAT, or included in the support plan', '£50/month + VAT, or included in the support plan']].map(([feature, sprint, strategic], i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-black/[0.02]'} data-id="element-631">
                        <td className="p-5 font-bold border border-black/5 text-xs uppercase tracking-wider" data-id="element-632">
                          {feature}
                        </td>
                        <td className="p-5 border border-black/5 text-ink/70" data-id="element-633">
                          {sprint}
                        </td>
                        <td className="p-5 border border-black/5 font-bold text-accent" data-id="element-634">
                          {strategic}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Timelines */}
            <section className="space-y-8" data-id="element-635">
              <div className="flex items-center gap-3" data-id="element-636">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-637" />
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-638">
                  Timelines
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-639">
                <div className="bg-[#fff5f0] p-10 rounded-[2rem] border border-black/5 space-y-6" data-id="element-640">
                  <div className="space-y-2" data-id="element-641">
                    <h3 className="text-2xl font-bold flex items-center gap-3" data-id="element-642">
                      <ClockIcon className="text-accent" size={24} data-id="element-643" />
                      Website Sprint
                    </h3>
                    <p className="text-sm font-bold uppercase tracking-widest text-ink/40" data-id="element-644">
                      14 days from kick-off
                    </p>
                  </div>
                  <div className="space-y-6 pt-6 border-t border-black/5" data-id="element-645">
                    <div className="space-y-2" data-id="element-646">
                      <p className="font-bold text-lg" data-id="element-647">Phase 1: Days 1 to 7</p>
                      <p className="text-base text-ink/70 leading-relaxed text-sm" data-id="element-648">
                        A live first version of the site featuring the agreed
                        key information, core messaging, contact details and
                        essential pages or sections needed to get you online
                        quickly. The exact structure of Phase 1 will be
                        discussed and agreed at the start of the project.
                      </p>
                    </div>
                    <div className="space-y-2" data-id="element-649">
                      <p className="font-bold text-lg" data-id="element-650">Phase 2: Days 8 to 14</p>
                      <p className="text-base text-ink/70 leading-relaxed text-sm" data-id="element-651">
                        Completion of the remaining agreed pages, refinements,
                        CMS setup, SEO setup and final handover.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-ink text-white p-10 rounded-[2rem] space-y-6" data-id="element-652">
                  <div className="space-y-2" data-id="element-653">
                    <h3 className="text-2xl font-bold flex items-center gap-3" data-id="element-654">
                      <BarChart3Icon className="text-accent" size={24} data-id="element-655" />
                      Strategic Website Build
                    </h3>
                    <p className="text-sm font-bold uppercase tracking-widest text-white/40" data-id="element-656">
                      4 weeks from kick-off
                    </p>
                  </div>
                  <p className="text-lg text-white/80 leading-relaxed pt-6 border-t border-white/10" data-id="element-657">
                    This option allows more time for strategy, planning, content
                    structure, refinement and a more custom final outcome.
                  </p>
                </div>
              </div>
            </section>

            {/* What is included */}
            <section className="space-y-12" data-id="element-658">
              <div className="flex items-center gap-3" data-id="element-659">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-660" />
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-661">
                  What is included
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-662">
                <div className="space-y-8" data-id="element-663">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-ink/40 border-b border-black/5 pb-4" data-id="element-664">
                    Website Sprint
                  </h3>
                  <ul className="space-y-4" data-id="element-665">
                    {['Predefined website layout adapted to your brand, content and priorities.', 'Up to 5 pages in total.', 'Mobile responsive build across desktop, tablet and mobile.', 'Basic SEO setup including meta titles, descriptions, image alt text and page hierarchy.', 'Contact form with enquiry delivery to your inbox.', 'Email confirmation notifications for both you and the enquirer.', 'WordPress CMS setup.', 'Documentation handover for CMS training.', 'Shutterstock images, up to 25.', 'Downloadable materials integration.', 'Launch handover and 7 days of post-launch support.'].map((item, i) => <li key={i} className="flex items-start gap-4 text-sm text-ink/70" data-id="element-666">
                        <CheckCircle2Icon size={18} className="mt-0.5 text-accent shrink-0" data-id="element-667" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="space-y-8" data-id="element-668">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-ink/40 border-b border-black/5 pb-4" data-id="element-669">
                    Strategic Website Build
                  </h3>
                  <ul className="space-y-4" data-id="element-670">
                    {['More tailored custom website build.', 'Up to 8 pages in total.', 'One strategy session to shape priorities, structure and direction.', 'Sitemap and clearer page structure.', 'Mobile responsive build across desktop, tablet and mobile.', 'Enhanced on-page SEO setup.', 'Contact form with enquiry delivery to your inbox.', 'Email confirmation notifications for both you and the enquirer.', 'WordPress CMS setup.', 'Live CMS training session plus documentation handover.', 'Shutterstock images, up to 25.', 'Downloadable materials integration.', 'Two consolidated revision rounds across the project.', 'Launch handover and 30 days of post-launch support.'].map((item, i) => <li key={i} className="flex items-start gap-4 text-sm text-ink/70" data-id="element-671">
                        <CheckCircle2Icon size={18} className="mt-0.5 text-accent shrink-0" data-id="element-672" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>
            </section>

            {/* Integrations & Out of Scope */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-673">
              <section className="space-y-8" data-id="element-674">
                <div className="flex items-center gap-3" data-id="element-675">
                  <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-676" />
                  <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-677">
                    Integrations
                  </h2>
                </div>
                <div className="space-y-4" data-id="element-678">
                  <div className="flex items-center gap-4 p-6 bg-white border border-black/5 rounded-2xl shadow-sm" data-id="element-679">
                    <div className="bg-accent/10 p-3 rounded-xl text-accent" data-id="element-680">
                      <ClockIcon size={24} data-id="element-681" />
                    </div>
                    <div data-id="element-682">
                      <p className="font-bold" data-id="element-683">Booking & Scheduling</p>
                      <p className="text-sm text-ink/60" data-id="element-684">
                        TidyCal or Calendly embedded into the site.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-6 bg-white border border-black/5 rounded-2xl shadow-sm" data-id="element-685">
                    <div className="bg-accent/10 p-3 rounded-xl text-accent" data-id="element-686">
                      <MessageSquareIcon size={24} data-id="element-687" />
                    </div>
                    <div data-id="element-688">
                      <p className="font-bold" data-id="element-689">Contact Forms</p>
                      <p className="text-sm text-ink/60" data-id="element-690">
                        Confirmation emails sent on every submission.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="space-y-8" data-id="element-691">
                <div className="flex items-center gap-3" data-id="element-692">
                  <div className="w-2.5 h-2.5 rounded-full bg-ink/20" data-id="element-693" />
                  <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-694">
                    Out of scope
                  </h2>
                </div>
                <ul className="space-y-4" data-id="element-695">
                  {['Website copywriting and written content creation.', 'Brand assets not supplied in final, ready-to-use format.', 'eCommerce or online ordering functionality.', 'Additional third-party integrations not listed above.'].map((item, i) => <li key={i} className="flex items-start gap-4 text-sm text-ink/50" data-id="element-696">
                      <AlertCircleIcon size={18} className="mt-0.5 text-ink/20 shrink-0" data-id="element-697" />
                      {item}
                    </li>)}
                </ul>
              </section>
            </div>

            {/* Hosting & Support Plan */}
            <section className="bg-ink text-white p-10 md:p-16 rounded-[3rem] space-y-12" data-id="element-698">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-699">
                <div className="space-y-6" data-id="element-700">
                  <h2 className="text-3xl font-bold tracking-tight" data-id="element-701">
                    Website support plan
                  </h2>
                  <p className="text-white/60 text-lg leading-relaxed" data-id="element-702">
                    Designed to keep the site maintained, secure and supported
                    after launch, without the client needing to manage the
                    technical side.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-4" data-id="element-703">
                    <div className="bg-white/5 px-6 py-4 rounded-2xl border border-white/10" data-id="element-704">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-accent mb-1" data-id="element-705">
                        Monthly Fee
                      </p>
                      <p className="text-2xl font-bold" data-id="element-706">
                        £150{' '}
                        <span className="text-sm font-normal text-white/40" data-id="element-707">
                          + VAT
                        </span>
                      </p>
                    </div>
                    <div className="bg-white/5 px-6 py-4 rounded-2xl border border-white/10" data-id="element-708">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-1" data-id="element-709">
                        Minimum Term
                      </p>
                      <p className="text-xl font-bold" data-id="element-710">12 Months</p>
                    </div>
                  </div>
                </div>
                <div className="bg-accent p-8 rounded-[2rem] text-ink space-y-4 flex flex-col justify-center relative overflow-hidden" data-id="element-711">
                  <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/20 rounded-full blur-3xl" data-id="element-712" />
                  <p className="text-xs uppercase tracking-widest font-bold text-ink/60" data-id="element-713">
                    Advance Payment Option
                  </p>
                  <h3 className="text-3xl font-bold leading-tight" data-id="element-714">
                    Pay for 12 months, receive 14 months.
                  </h3>
                  <p className="text-sm font-medium opacity-80" data-id="element-715">
                    Get 2 months of professional support and hosting free when
                    paying in advance.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 pt-12 border-t border-white/10" data-id="element-716">
                {[{
                title: 'Hosting',
                desc: 'Website hosting included within the plan',
                icon: GlobeIcon
              }, {
                title: 'Platform Updates',
                desc: 'The site stays current, compatible and protected',
                icon: SettingsIcon
              }, {
                title: 'Security & Backups',
                desc: 'The site is backed up and monitored for issues',
                icon: ShieldCheckIcon
              }, {
                title: 'Content Updates',
                desc: 'Up to 4 small updates per month handled for you',
                icon: LayoutIcon
              }, {
                title: 'Priority Support',
                desc: 'Fast access to support when needed via email',
                icon: MessageSquareIcon
              }, {
                title: 'Monthly Reporting',
                desc: 'Clear visibility on what has been done',
                icon: BarChart3Icon
              }].map((item, i) => <div key={i} className="space-y-3 group" data-id="element-717">
                    <div className="flex items-center gap-3 font-bold text-lg" data-id="element-718">
                      <div className="p-2 bg-white/5 rounded-lg group-hover:bg-accent group-hover:text-ink transition-all" data-id="element-719">
                        <item.icon size={20} data-id="element-720" />
                      </div>
                      {item.title}
                    </div>
                    <p className="text-sm text-white/50 leading-relaxed pl-11" data-id="element-721">
                      {item.desc}
                    </p>
                  </div>)}
              </div>

              <div className="pt-8 border-t border-white/10" data-id="element-722">
                <h3 className="text-sm font-bold uppercase tracking-widest text-white/40 mb-6" data-id="element-723">
                  Support pricing breakdown
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-724">
                  <div className="flex justify-between items-center p-6 bg-white/5 rounded-2xl border border-white/10" data-id="element-725">
                    <span className="font-bold" data-id="element-726">Monthly (12-month term)</span>
                    <span className="font-bold text-xl" data-id="element-727">£1,800 + VAT</span>
                  </div>
                  <div className="flex justify-between items-center p-6 bg-accent rounded-2xl text-ink" data-id="element-728">
                    <span className="font-bold" data-id="element-729">
                      12 months paid in advance (2 months free)
                    </span>
                    <span className="font-bold text-xl text-ink" data-id="element-730">
                      £1,800 + VAT
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Intellectual Property & Workflow */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-731">
              <section className="space-y-8" data-id="element-732">
                <div className="flex items-center gap-3" data-id="element-733">
                  <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-734" />
                  <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-735">
                    Intellectual property
                  </h2>
                </div>
                <div className="space-y-4 text-sm leading-relaxed text-ink/70" data-id="element-736">
                  <p data-id="element-737">
                    Upon receipt of full and final payment, all intellectual
                    property rights for the completed website, including final
                    design files, page layouts and developed assets, will be
                    assigned in full to{' '}
                    <span className="font-bold text-ink" data-id="element-738">Anita Oberoi</span>.
                  </p>
                  <p data-id="element-739">
                    AVY® Consultancy Limited retains the right to showcase the
                    completed work within its portfolio, website and marketing
                    materials unless confidentiality is requested and agreed in
                    writing before the project begins.
                  </p>
                </div>
              </section>

              <section className="space-y-8" data-id="element-740">
                <div className="flex items-center gap-3" data-id="element-741">
                  <div className="w-2.5 h-2.5 rounded-full bg-accent" data-id="element-742" />
                  <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-743">
                    How the project will run
                  </h2>
                </div>
                <ul className="space-y-4" data-id="element-744">
                  {['Review content within 48 hours of kicks off.', 'Feedback must be provided within 2 working days of each stage.', 'One consolidated round of revisions is included for the Website Sprint.', 'Two consolidated revision rounds are included for the Strategic Build.', 'Additional changes outside the agreed scope will be quoted separately.', 'Delays caused by late content or feedback may affect the delivery timeline.'].map((item, i) => <li key={i} className="flex items-start gap-4 text-sm text-ink/70" data-id="element-745">
                      <InfoIcon size={18} className="mt-0.5 text-accent shrink-0" data-id="element-746" />
                      {item}
                    </li>)}
                </ul>
              </section>
            </div>

            {/* Terms & Conditions */}
            <section className="space-y-12 pt-12 border-t border-black/5" data-id="element-747">
              <div className="flex items-center gap-3" data-id="element-748">
                <div className="w-2.5 h-2.5 rounded-full bg-ink/20" data-id="element-749" />
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-ink" data-id="element-750">
                  Terms and conditions
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 text-xs leading-relaxed text-ink/60" data-id="element-751">
                {[{
                t: '1. Scope of services',
                c: 'Work is limited to what is expressly outlined in this proposal. Anything outside the agreed scope may require a separate agreement or additional fees.'
              }, {
                t: '2. Project timeline',
                c: 'Timelines depend on timely feedback, content and approvals from the client. Delays caused by the client may impact delivery and AVY® accepts no liability for resulting timeline changes.'
              }, {
                t: '3. Fees and payment',
                c: 'A deposit is required before work begins. The remaining balance is due upon project completion. Invoices must be paid within the agreed payment period. Late payment may result in work being paused.'
              }, {
                t: '4. Revisions',
                c: 'A reasonable number of revisions are included as outlined in this proposal. Additional revisions or out-of-scope changes are chargeable at standard rates.'
              }, {
                t: '5. Intellectual property',
                c: 'Upon full payment, ownership of the final approved deliverables transfers to the client. AVY® retains all unused concepts, drafts and working files, and reserves the right to use completed work for portfolio and promotional purposes unless confidentiality is agreed in writing.'
              }, {
                t: '6. Third-party materials',
                c: 'Third-party assets such as fonts, stock imagery or software remain subject to their own licence terms. Ongoing licence responsibilities sit with the client unless otherwise agreed in writing.'
              }, {
                t: '7. Confidentiality',
                c: 'Both parties agree to keep non-public project information confidential unless disclosure is required by law or agreed in writing.'
              }, {
                t: '8. Limitation of liability',
                c: 'Services are carried out with reasonable skill and care. Liability is limited to the total fees paid under this proposal. AVY® is not liable for indirect losses, loss of profit or business interruption.'
              }, {
                t: '9. Termination',
                c: 'Either party may terminate with written notice. Fees for work completed up to that point remain payable. Deposits are non-refundable once work has commenced.'
              }, {
                t: '10. Governing law',
                c: 'These Terms are governed by the laws of England and Wales. The courts of England and Wales hold exclusive jurisdiction.'
              }].map((item, i) => <div key={i} className="space-y-2" data-id="element-752">
                    <p className="font-bold text-ink uppercase tracking-wider" data-id="element-753">
                      {item.t}
                    </p>
                    <p data-id="element-754">{item.c}</p>
                  </div>)}
              </div>
            </section>

            {/* Next Steps */}
            <section className="bg-[#fff5f0] p-10 md:p-16 rounded-[3rem] border border-black/5 space-y-10" data-id="element-755">
              <div className="space-y-4" data-id="element-756">
                <h2 className="text-3xl font-bold tracking-tight text-ink" data-id="element-757">
                  Next steps
                </h2>
                <p className="text-ink/60 text-lg" data-id="element-758">
                  To confirm your place in the schedule:
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-759">
                {['Review the two website options.', 'Confirm which option you would like to proceed with.', 'Pay the deposit to secure the start date.', 'Review content within 48 hours of kicks off.'].map((step, i) => <div key={i} className="flex items-center gap-5 p-6 bg-white rounded-2xl border border-black/5 shadow-sm" data-id="element-760">
                    <div className="w-10 h-10 rounded-full bg-accent text-white flex items-center justify-center text-lg font-bold shrink-0" data-id="element-761">
                      {i + 1}
                    </div>
                    <p className="font-bold text-ink" data-id="element-762">{step}</p>
                  </div>)}
              </div>
              <div className="pt-12 border-t border-black/5 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-763">
                <div className="text-center md:text-left" data-id="element-764">
                  <p className="text-sm font-bold uppercase tracking-widest text-ink/40" data-id="element-765">
                    AVY® Consultancy Limited
                  </p>
                  <p className="text-xs text-ink/40" data-id="element-766">
                    124 City Road, London, EC1V 2NX, United Kingdom
                  </p>
                </div>
                <img src={LOGO_URL} alt="Avy Logo" className="h-8 w-auto opacity-20" data-id="element-767" />
              </div>
            </section>
          </div>
        </main>

        <Footer data-id="element-768" />
      </div>
    </div>;
}